import { BACKGROUND_COLOR_DEFAULT, TEXT_COLOR_DEFAULT } from '../../constants/colors';
import { BrandKitStore, PlatformBrandKitStore } from '../../types/brand-kit-types';
import { Clip } from '../../types/clip-types';
import { SelectionElementType } from '../../types/RemotionPlayerConfigTypes/CompositionConfig';
import {
  SpeakerLabel,
  SpeakerLabelElementType,
  SpeakerLabelTextInterface
} from '../../types/RemotionPlayerConfigTypes/Element/VideoElement';
import {
  getFontSizeForPreset,
  DEFAULT_NAME_FONT_SIZE_PRESET,
  LEGACY_NAME_FONT_SIZE,
  DEFAULT_TITLE_FONT_SIZE_PRESET,
  LEGACY_TITLE_FONT_SIZE
} from '../../types/speaker-label-font-size';
import { SpeakerLabelTransitionEffect } from '../../types/transitions';
import { VideoFeed } from '../../types/video-feed-types';
import { getFontObject } from '../../utils/fontUtils';
import { getSpeakerLabelBorderRadiusFull } from '../../utils/speakerLabelUtils';
import { getSpeakerNameAndTitle } from '../../utils/speakerUtils';
import { getSpeakerLabelConfigForAudiograms } from './getSpeakerLabelConfigForAudiograms';

export const getSpeakerLabelConfig = ({
  clip,
  videoFeed,
  clipBrandKitStore,
  brandKitStore,
  platformBrandKit,
  organization,
  isPlatformBrandKitEnabled
}: {
  videoFeed: VideoFeed;
  clip: Clip;
  isPlatformBrandKitEnabled: boolean;
  clipBrandKitStore: BrandKitStore;
  brandKitStore: BrandKitStore;
  platformBrandKit: PlatformBrandKitStore;
  organization?: string | null | undefined;
}) => {
  const speaker = videoFeed.speakerDetails;
  const { name, title } = getSpeakerNameAndTitle(speaker);

  const assetMetadata = clip.asset_metadata;

  const fontObject = getFontObject({
    clip,
    fontTarget: SelectionElementType.SPEAKER_LABEL,
    clipBrandKitStore,
    brandKitStore,
    platformBrandKit,
    organization,
    isPlatformBrandKitEnabled
  });

  const backgroundColor =
    assetMetadata?.speaker_label_styles?.background_color ||
    assetMetadata?.magicLayout?.backgroundColor ||
    BACKGROUND_COLOR_DEFAULT;

  const textColor =
    assetMetadata?.speaker_label_styles?.text_color || assetMetadata?.magicLayout?.textColor || TEXT_COLOR_DEFAULT;

  const useAnimatedSpeakerCards = !!assetMetadata?.speaker_label_styles?.transitions?.in;

  const borderRadius = useAnimatedSpeakerCards
    ? assetMetadata?.speaker_label_styles?.containerStyle?.borderRadius
    : assetMetadata?.speaker_label_styles?.border_radius;

  const textMarginHorizontal = useAnimatedSpeakerCards
    ? undefined
    : borderRadius === getSpeakerLabelBorderRadiusFull()
      ? '3cqw'
      : '2cqw';

  const padding = useAnimatedSpeakerCards
    ? borderRadius === getSpeakerLabelBorderRadiusFull()
      ? '1cqw 2cqw'
      : '1cqw 1cqw'
    : '1cqi';

  const nameStyle = assetMetadata?.speaker_label_styles?.nameStyle;
  const titleStyle = assetMetadata?.speaker_label_styles?.titleStyle;

  const nameFontSize = useAnimatedSpeakerCards
    ? getFontSizeForPreset(nameStyle?.fontSizePreset ?? DEFAULT_NAME_FONT_SIZE_PRESET)
    : LEGACY_NAME_FONT_SIZE;

  const titleFontSize = useAnimatedSpeakerCards
    ? getFontSizeForPreset(titleStyle?.fontSizePreset ?? DEFAULT_TITLE_FONT_SIZE_PRESET)
    : LEGACY_TITLE_FONT_SIZE;

  const {
    fontSizePreset: _nameFontSizePreset,
    fontSize: _nameFontSize,
    fontFamily: _nameFontFamily,
    ...nameStyleRest
  } = nameStyle || {};

  const {
    fontSizePreset: _titleFontSizePreset,
    fontSize: _titleFontSize,
    fontFamily: _titleFontFamily,
    ...titleStyleRest
  } = titleStyle || {};

  const titleFontObject = getFontObject({
    clip,
    fontTarget: SelectionElementType.SPEAKER_LABEL_TITLE,
    clipBrandKitStore,
    brandKitStore,
    platformBrandKit,
    organization,
    isPlatformBrandKitEnabled
  });

  const speakerLabel: SpeakerLabel =
    clip.asset_metadata.layout === 'AUDIOGRAM'
      ? getSpeakerLabelConfigForAudiograms({
          speaker: speaker!,
          clip,
          clipBrandKitStore,
          brandKitStore,
          platformBrandKit,
          organization,
          isPlatformBrandKitEnabled
        })
      : {
          style: {
            position: 'absolute',
            bottom: '0.7cqw',
            left: '0.7cqw',
            minWidth: assetMetadata.speaker_label_styles?.containerStyle ? undefined : '40cqw',
            maxWidth: assetMetadata.speaker_label_styles?.containerStyle ? '90cqw' : '50cqw',
            alignItems: 'flex-start',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            lineHeight: 'none',
            backgroundColor,
            color: textColor,
            borderRadius,
            padding,
            display: 'flex',
            flexDirection: 'column',
            fontFamily: fontObject?.font?.name,
            zIndex: 10,
            ...(assetMetadata.speaker_label_styles?.containerStyle || {})
          },
          type: SpeakerLabelElementType.CONTAINER,
          elements: [
            {
              type: SpeakerLabelElementType.TEXT,
              content: name,
              style: {
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontSize: nameFontSize,
                lineHeight: 'none',
                fontFamily: 'inherit',
                maxWidth: '100%',
                marginLeft: textMarginHorizontal,
                marginRight: textMarginHorizontal,
                ...nameStyleRest
              }
            },
            ...(title
              ? [
                  {
                    type: SpeakerLabelElementType.TEXT,
                    content: title,
                    style: {
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      fontSize: titleFontSize,
                      lineHeight: 'none',
                      fontFamily: titleFontObject?.font?.name,
                      maxWidth: '100%',
                      marginLeft: textMarginHorizontal,
                      marginRight: textMarginHorizontal,
                      ...titleStyleRest
                    }
                  }
                ]
              : [])
          ] as SpeakerLabelTextInterface[],
          transitions: useAnimatedSpeakerCards
            ? assetMetadata.speaker_label_styles?.transitions || {
                in: SpeakerLabelTransitionEffect.None,
                out: SpeakerLabelTransitionEffect.None,
                showAfter: 0,
                hideAfter: undefined,
                duration: 0.3,
                showAgainOnNewSpeaker: false
              }
            : undefined,
          ...fontObject,
          ...(titleFontObject?.font && { titleFont: titleFontObject?.font })
        };

  return { speakerLabel, speakerDetails: speaker ?? null };
};
